import {ChangeDetectionStrategy, Component, inject, OnInit} from '@angular/core';
import {MatTree, MatTreeModule} from '@angular/material/tree';
import {ActivatedRoute} from '@angular/router';
import {NbButtonModule} from '@nebular/theme';
import {TuiIcon} from '@taiga-ui/core';
import {BaseComponent, LazyCatalog} from 'compob2b-libs';
import {take, takeUntil} from 'rxjs/operators';
import {CustomerCatalogService} from '../services/customer-catalog.service';
import {CustomerDynamicDataSource} from './customer-dynamic-data-source';
import {navigationSnapshot$} from '../../shared/routing/route-state.stream';
import {catalogIdFromPath, catalogUrl} from '../../shared/routing/url-builder';

@Component({
  selector: 'app-modal-catalog',
  templateUrl: './modal-catalog.component.html',
  styleUrl: './modal-catalog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NbButtonModule,
    TuiIcon,
    MatTreeModule,
  ],
})

export class ModalCatalogComponent extends BaseComponent implements OnInit {

  private readonly catalogService = inject(CustomerCatalogService);
  private readonly route = inject(ActivatedRoute);

  dataSource: CustomerDynamicDataSource;
  isShowBlock = false;
  catalogId: string;
  limit: string;
  region: string;

  getLevel = (node: LazyCatalog) => node.level;

  ngOnInit() {
    // Компонент живёт в лейауте, поэтому его `ActivatedRoute` — корневой: path-параметра
    // каталожного роута он не видит. Читаем согласованный снапшот после каждой навигации
    // (общий `route-state.stream.ts`).
    //
    // В ключ дедупликации входят И limit/region: версия, которая отбрасывала повтор
    // «только по категории», не замечала появления `?region=…` в URL (его дописывает
    // листинг навигацией, которая категорию не меняет). Клик по дереву тогда уходил с
    // `region: null` — регион выпадал из URL, листинг возвращал его обратно, и на каждый
    // клик выходила лишняя навигация с лишним запросом товаров.
    navigationSnapshot$(
      this.router,
      () => {
        const query = this.route.snapshot.queryParamMap;
        const id = catalogIdFromPath(this.router.url);
        return {
          catalogId: id === null ? null : String(id),
          limit: query.get('limit'),
          region: query.get('region'),
        };
      },
      (a, b) => a.catalogId === b.catalogId && a.limit === b.limit && a.region === b.region,
    )
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(({catalogId, limit, region}) => {
        this.catalogId = catalogId;
        this.limit = limit;
        this.region = region;
      });

    this.dataSource = new CustomerDynamicDataSource(this.catalogService, this.unsubscribe);
    this.catalogService.initCatalogTree()
      .pipe(take(1))
      .subscribe(tree => this.dataSource.data = tree);
  }

  onExpandedChange(node: LazyCatalog, expanded: boolean) {
    this.dataSource.toggleNode(node, expanded);
    this.cdr.markForCheck();
  }

  /**
   * Канонический путь категории — он же `href` ссылки, он же цель SPA-навигации.
   * Сессионные фильтры (limit/region) здесь НЕ добавляем: иначе краулер
   * индексирует вариативные URL. Рантайм-навигация доклеивает их отдельно.
   *
   * Узел несёт и `id`, и `header`, поэтому строим ПОЛНУЮ форму со slug'ом. Прежняя
   * навигация шла через `entityPathByKey('catalog', this.catalogId)` — форму без
   * slug, — и каждый клик по дереву получал в ответ 301 резолвера на тот же URL со
   * slug'ом. Лишний хоп на самом частом действии в каталоге.
   */
  catalogHref(node: LazyCatalog): string {
    return node?.id != null ? catalogUrl({id: node.id, header: node.header}) : '/catalog';
  }

  // Активный раздел подсвечивается в дереве. catalogId приходит строкой из URL,
  // node.id — number, поэтому сравниваем через String() (вместо прежнего ==).
  isActive(node: LazyCatalog): boolean {
    return this.catalogId === String(node.id);
  }

  navToCatalog(node: LazyCatalog, tree: MatTree<LazyCatalog>, event?: MouseEvent) {
    // Модификаторный/не-левый клик отдаём браузеру — пусть откроет href в
    // новой вкладке/окне (как у обычной ссылки). Перехватываем в SPA только
    // чистый левый клик и гасим его дефолтный full-page переход.
    if (event && (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey)) {
      return;
    }
    event?.preventDefault();
    // Оптимистичная подсветка выбранного узла: общий поток даст то же значение,
    // но только после NavigationEnd — без этой строки узел на миг гаснет.
    this.catalogId = node?.id != null ? String(node.id) : null;
    // Закрываем dropdown только на листовых узлах — иначе на мобиле он
    // остаётся открытым поверх каталога после navigate. На узловых разделах
    // оставляем дерево раскрытым, чтобы можно было сразу перейти к соседу.
    if (node?.lastLevel) {
      this.isShowBlock = false;
    } else if (node) {
      // Узел с подразделами: переключаем раскрытие. mat-tree эмитит
      // (expandedChange), которое уже подписано на onExpandedChange и
      // дёрнет dataSource.toggleNode для подгрузки children.
      tree.toggle(node);
    }
    // Навигация по клику сохраняет сессионные фильтры limit/region.
    void this.router.navigateByUrl(
      this.router.createUrlTree([this.catalogHref(node)], {
        queryParams: {
          limit: this.limit,
          region: this.region,
        },
      }),
    );
  }

  showBlock(show: boolean) {
    this.isShowBlock = show;
  }

  // Toggle dropdown — клик по кнопке «Каталог» открывает/закрывает выпадашку.
  // На десктопе раньше шёл прямой переход в /catalog, но на мобиле тогда
  // dropdown недоступен. Теперь оба сценария: tap на кнопку открывает дерево,
  // tap на пункт дерева — navigate.
  toggleBlock() {
    this.isShowBlock = !this.isShowBlock;
  }

}
